import axios from 'axios';

// Use relative URL when in development to work with proxy
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://api.prepstop.com')
  : ''; // Empty string means use proxy in development

// Type Definitions
export interface PharmacyKPIs {
  totalRegistered: number;
  totalAssigned?: number; // For backward compatibility
  activeOnPrep: number;
  pending: number;
  readyForTelehealth: number;
  pendingBreakdown: {
    hivTest: number;
    insurance: number;
    telehealth: number;
  };
  pendingHIV?: number; // For backward compatibility
  pendingInsurance: number;
  readyForFulfillment?: number; // For backward compatibility
  renewalsDue: number;
  refills: number;
  medicationType: {
    oral: number;
    injectable: number;
  };
  byMarket?: Record<string, number>;
  conversionRate?: string;
  monthlyGrowth?: Array<{
    month: string;
    count: number;
  }>;
}

export interface QueuePatient {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  market: string;
  display_status: string;
  prep_type: string;
  medication_name: string | null;
  insurance_status: string | null;
  insurance_verified: boolean;
  hiv_test_completed: boolean;
  hiv_test_result: string | null;
  telehealth_completed: boolean;
  prescription_issued: boolean;
  medication_shipped: boolean;
  injection_scheduled: boolean;
  prep_administered: boolean;
  next_refill_date: string | null;
  next_action_due: string | null;
  daysInQueue: number;
  updatedAt: string;
  currentCase: {
    id: number;
    status: string;
  } | null;
}

export type QueueType = 'pendingInsurance' | 'hivResultPending' | 'telehealthCompleted' | 'readyForFulfillment' | 'renewalsDue' | 'all';

export interface InsuranceDocument {
  filename: string;
  url: string;
  presignedUrl?: string;
  uploadedAt: string;
  size?: number;
  mimeType?: string;
}

export interface InsuranceDocumentsResponse {
  documents: InsuranceDocument[];
  uploadedBy?: {
    name: string;
    organization: string;
  };
  uploadedAt?: string;
  message?: string;
}

class PharmacyDashboardService {
  private baseURL = `${API_BASE_URL}/api/dashboard/pharmacy`;

  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true, // Include cookies for session-based auth
    };
  }

  /**
   * Get pharmacy partner KPIs
   */
  async getKPIs(filters?: { market?: string | null }): Promise<PharmacyKPIs> {
    const params = filters ? filters : {};
    const response = await axios.get(`${this.baseURL}/kpis`, {
      ...this.getAuthHeaders(),
      params
    });
    return response.data;
  }

  /**
   * Get patients in specific queue
   */
  async getQueuePatients(queueType: QueueType): Promise<QueuePatient[]> {
    const response = await axios.get(`${this.baseURL}/queue/${queueType}`, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Verify patient insurance
   */
  async verifyInsurance(patientId: number): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(`${this.baseURL}/verify-insurance/${patientId}`, {}, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Mark medication as shipped
   */
  async markShipped(patientId: number, data: { trackingNumber?: string; shippedDate?: string }): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(`${this.baseURL}/mark-shipped/${patientId}`, data, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Schedule injection appointment
   */
  async scheduleInjection(patientId: number, data: { injectionDate: string; nextInjectionDate: string }): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(`${this.baseURL}/schedule-injection/${patientId}`, data, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get single patient detail (uses existing admin endpoint)
   */
  async getPatientDetail(patientId: number): Promise<any> {
    const url = `${API_BASE_URL}/api/dashboard/patients/${patientId}`;
    console.log('[pharmacyDashboardService] getPatientDetail called with patientId:', patientId);
    console.log('[pharmacyDashboardService] API_BASE_URL:', API_BASE_URL);
    console.log('[pharmacyDashboardService] Full URL:', url);
    console.log('[pharmacyDashboardService] NODE_ENV:', process.env.NODE_ENV);
    const response = await axios.get(url, this.getAuthHeaders());
    console.log('[pharmacyDashboardService] Response status:', response.status);
    return response.data;
  }

  /**
   * Get insurance documents for a patient with presigned URLs
   */
  async getInsuranceDocuments(patientId: number): Promise<InsuranceDocumentsResponse> {
    const response = await axios.get(`${this.baseURL}/insurance-documents/${patientId}`, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Upload document (uses generic patient-documents endpoint)
   */
  async uploadDocument(data: {
    patientId: number | string;
    documents: File[];
    documentTypes: string[];
    category: string;
    notes?: string;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('patientId', String(data.patientId));
    data.documents.forEach((doc) => formData.append('documents', doc));
    data.documentTypes.forEach((type) => formData.append('documentTypes', type));
    formData.append('category', data.category);
    if (data.notes) formData.append('notes', data.notes);

    const response = await axios.post(
      `${API_BASE_URL}/api/patient-documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(localStorage.getItem('authToken') && { Authorization: `Bearer ${localStorage.getItem('authToken')}` }),
        },
        withCredentials: true,
      }
    );
    return response.data;
  }

  /**
   * Update insurance status for a patient
   */
  async updateInsuranceStatus(patientId: string, status: string): Promise<void> {
    const response = await axios.patch(
      `${API_BASE_URL}/api/patient/${patientId}/insurance-status`,
      { insurance_status: status },
      this.getAuthHeaders()
    );
    return response.data;
  }
}

export default new PharmacyDashboardService();
